import * as React from "react";
import { EdgeProps, getBezierPath, EdgeLabelRenderer, BaseEdge, Edge as _Edge } from "reactflow";
import { EdgeClassification } from "../../../utils/decisiontree/graph-builder";
import { ORPHAN_PURPLE, CROSS_ORPHAN_BLUE, CROSS_CONNECT_AMBER } from "../decision-tree-colors";

// Label spread — siblings spread apart vertically to avoid overlap
const LABEL_SPREAD = 40;

/**
 * Edge color scheme based on classification.
 */
const EDGE_COLORS: Record<EdgeClassification, string | undefined> = {
    "connected": undefined,
    "orphan": ORPHAN_PURPLE,
    "cross-orphan": CROSS_ORPHAN_BLUE,
    "cross-connect": CROSS_CONNECT_AMBER
};

const EDGE_DASH_PATTERNS: Record<EdgeClassification, string | undefined> = {
    "connected": undefined,
    "orphan": "5,5",
    "cross-orphan": "8,4",
    "cross-connect": "3,3"
};

/** CSS class modifier for label background by classification */
const LABEL_CLASS_MODIFIER: Record<EdgeClassification, string> = {
    "connected": "",
    "orphan": " edge-label--orphan",
    "cross-orphan": " edge-label--cross-orphan",
    "cross-connect": ""
};

export namespace CustomEdge {

    export const Type = "decisionTreeEdge";

    export interface Data {
        optionText: string;
        childIndex?: number;
        totalChildren?: number;
        classification?: EdgeClassification;
    }

    export type Edge = _Edge<Data>;

    export const Comp: React.FC<EdgeProps> = ({
        id, sourceX, sourceY, targetX, targetY,
        sourcePosition, targetPosition,
        data, style = {}, markerEnd
    }) => {
        // Track hover and focus independently to avoid inconsistent state when both are active.
        // Needed because EdgeLabelRenderer renders labels in a separate HTML overlay
        // outside the SVG where edge paths live — CSS sibling selectors cannot cross this boundary.
        const [isMouseOver, setIsMouseOver] = React.useState(false);
        const [isFocused, setIsFocused] = React.useState(false);
        const isHighlighted = isMouseOver || isFocused;

        const [edgePath, labelX, labelY] = getBezierPath({
            sourceX, sourceY, sourcePosition,
            targetX, targetY, targetPosition
        });

        // Spread sibling labels apart
        const totalChildren = data?.totalChildren ?? 1;
        const childIndex = data?.childIndex ?? 0;
        const labelOffsetY = totalChildren > 1
            ? (childIndex / (totalChildren - 1) - 0.5) * LABEL_SPREAD
            : 0;

        // Classification-based styling
        const classification: EdgeClassification = data?.classification ?? "connected";
        const baseStrokeColor = EDGE_COLORS[classification];
        const strokeDasharray = EDGE_DASH_PATTERNS[classification];
        const labelClassName = `edge-label${LABEL_CLASS_MODIFIER[classification]}`;

        return (
            <>
                <BaseEdge
                    path={edgePath}
                    markerEnd={markerEnd}
                    style={{
                        ...style,
                        stroke: isHighlighted ? "red" : baseStrokeColor,
                        strokeDasharray
                    }}
                />
                <EdgeLabelRenderer>
                    <div
                        className={`${labelClassName} nopan`}
                        tabIndex={0}
                        style={{
                            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY + labelOffsetY}px)`
                        }}
                        title={data?.optionText || ""}
                        onMouseEnter={() => setIsMouseOver(true)}
                        onMouseLeave={() => setIsMouseOver(false)}
                        onFocus={(e) => { if (e.target.matches(":focus-visible")) setIsFocused(true); }}
                        onBlur={() => setIsFocused(false)}>
                        {data?.optionText || ""}
                    </div>
                </EdgeLabelRenderer>
            </>
        );
    };

    export const List = { [Type]: CustomEdge.Comp };
}
